import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Plus, Trash2, Save, Shield, AlertTriangle, TrendingUp, Target,
  Landmark, DollarSign, Users, Cpu, Leaf, Scale
} from 'lucide-react';
import { toast } from 'sonner';

const API = BACKEND_URL;

const SWOT_CONFIG = [
  { key: 'strengths', label: 'Strengths', color: '#10B981', bg: 'bg-green-50 border-green-200', icon: Shield, description: 'Internal positive attributes' },
  { key: 'weaknesses', label: 'Weaknesses', color: '#EF4444', bg: 'bg-red-50 border-red-200', icon: AlertTriangle, description: 'Internal areas for improvement' },
  { key: 'opportunities', label: 'Opportunities', color: '#3B82F6', bg: 'bg-blue-50 border-blue-200', icon: TrendingUp, description: 'External favorable factors' },
  { key: 'threats', label: 'Threats', color: '#F59E0B', bg: 'bg-amber-50 border-amber-200', icon: Target, description: 'External risks and challenges' },
];

const PESTEL_CONFIG = [
  { key: 'political', label: 'Political', color: '#6366F1', bg: 'bg-indigo-50 border-indigo-200', icon: Landmark },
  { key: 'economic', label: 'Economic', color: '#10B981', bg: 'bg-emerald-50 border-emerald-200', icon: DollarSign },
  { key: 'social', label: 'Social', color: '#3B82F6', bg: 'bg-blue-50 border-blue-200', icon: Users },
  { key: 'technological', label: 'Technological', color: '#8B5CF6', bg: 'bg-violet-50 border-violet-200', icon: Cpu },
  { key: 'environmental', label: 'Environmental', color: '#14B8A6', bg: 'bg-teal-50 border-teal-200', icon: Leaf },
  { key: 'legal', label: 'Legal', color: '#F59E0B', bg: 'bg-amber-50 border-amber-200', icon: Scale },
];

const PDAnalysis = ({ ideaId, initialSwot, initialPestel, canManage }) => {
  const [swot, setSwot] = useState(initialSwot || { strengths: [], weaknesses: [], opportunities: [], threats: [] });
  const [pestel, setPestel] = useState(initialPestel || { political: [], economic: [], social: [], technological: [], environmental: [], legal: [] });
  const [editingSwot, setEditingSwot] = useState(false);
  const [editingPestel, setEditingPestel] = useState(false);
  const [editSwot, setEditSwot] = useState(null);
  const [editPestel, setEditPestel] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchSwot = async () => {
    try {
      const res = await axios.get(`${API}/api/participatory-design/ideas/${ideaId}/swot`, { headers });
      setSwot(res.data);
    } catch { /* ignore */ }
  };

  const fetchPestel = async () => {
    try {
      const res = await axios.get(`${API}/api/participatory-design/ideas/${ideaId}/pestel`, { headers });
      setPestel(res.data);
    } catch { /* ignore */ }
  };

  const openSwotEditor = () => { setEditSwot(JSON.parse(JSON.stringify(swot))); setEditingSwot(true); };
  const openPestelEditor = () => { setEditPestel(JSON.parse(JSON.stringify(pestel))); setEditingPestel(true); };

  const handleSaveSwot = async () => {
    try {
      const res = await axios.post(`${API}/api/participatory-design/ideas/${ideaId}/swot`, editSwot, { headers });
      setSwot(res.data);
      setEditingSwot(false);
      toast.success('SWOT analysis saved');
    } catch { toast.error('Failed to save'); }
  };

  const handleSavePestel = async () => {
    try {
      const res = await axios.post(`${API}/api/participatory-design/ideas/${ideaId}/pestel`, editPestel, { headers });
      setPestel(res.data);
      setEditingPestel(false);
      toast.success('PESTEL analysis saved');
    } catch { toast.error('Failed to save'); }
  };

  const addItem = (setter, key) => setter(prev => ({ ...prev, [key]: [...(prev[key] || []), ''] }));
  const updateItem = (setter, key, idx, val) => setter(prev => { const arr = [...(prev[key] || [])]; arr[idx] = val; return { ...prev, [key]: arr }; });
  const removeItem = (setter, key, idx) => setter(prev => ({ ...prev, [key]: (prev[key] || []).filter((_, i) => i !== idx) }));

  const hasSwotData = SWOT_CONFIG.some(c => (swot[c.key] || []).length > 0);
  const hasPestelData = PESTEL_CONFIG.some(c => (pestel[c.key] || []).length > 0);

  // ======= SWOT Grid Renderer =======
  const renderSwotGrid = (data, config, editable, setter) => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {config.map(c => {
        const Icon = c.icon;
        const items = data[c.key] || [];
        return (
          <Card key={c.key} className={`border ${c.bg}`} data-testid={`swot-${c.key}`}>
            <CardContent className="p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <Icon className="h-4 w-4" style={{ color: c.color }} />
                  <span className="text-sm font-semibold" style={{ color: c.color }}>{c.label}</span>
                  <Badge variant="outline" className="text-xs">{items.length}</Badge>
                </div>
                {editable && (
                  <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => addItem(setter, c.key)} data-testid={`add-${c.key}`}>
                    <Plus className="h-3 w-3" />
                  </Button>
                )}
              </div>
              {items.length === 0 && !editable && <p className="text-xs text-gray-500 italic">No items</p>}
              <div className="space-y-1">
                {items.map((item, idx) => editable ? (
                  <div key={idx} className="flex gap-1">
                    <Input value={item} onChange={e => updateItem(setter, c.key, idx, e.target.value)} className="h-7 text-xs bg-white" placeholder={`${c.label} item...`} />
                    <Button size="sm" variant="ghost" className="text-red-400 h-7" onClick={() => removeItem(setter, c.key, idx)}><Trash2 className="h-2.5 w-2.5" /></Button>
                  </div>
                ) : (
                  <div key={idx} className="flex items-start gap-1.5 text-xs text-gray-700">
                    <span className="mt-0.5 shrink-0" style={{ color: c.color }}>-</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );

  // ======= PESTEL Grid Renderer =======
  const renderPestelGrid = (data, config, editable, setter) => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {config.map(c => {
        const Icon = c.icon;
        const items = data[c.key] || [];
        return (
          <Card key={c.key} className={`border ${c.bg}`} data-testid={`pestel-${c.key}`}>
            <CardContent className="p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <Icon className="h-4 w-4" style={{ color: c.color }} />
                  <span className="text-xs font-semibold" style={{ color: c.color }}>{c.label}</span>
                  <Badge variant="outline" className="text-xs">{items.length}</Badge>
                </div>
                {editable && (
                  <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => addItem(setter, c.key)} data-testid={`add-pestel-${c.key}`}>
                    <Plus className="h-3 w-3" />
                  </Button>
                )}
              </div>
              {items.length === 0 && !editable && <p className="text-xs text-gray-500 italic">No items</p>}
              <div className="space-y-1">
                {items.map((item, idx) => editable ? (
                  <div key={idx} className="flex gap-1">
                    <Input value={item} onChange={e => updateItem(setter, c.key, idx, e.target.value)} className="h-7 text-xs bg-white" placeholder={`${c.label} factor...`} />
                    <Button size="sm" variant="ghost" className="text-red-400 h-7" onClick={() => removeItem(setter, c.key, idx)}><Trash2 className="h-2.5 w-2.5" /></Button>
                  </div>
                ) : (
                  <div key={idx} className="flex items-start gap-1.5 text-xs text-gray-700">
                    <span className="mt-0.5 shrink-0" style={{ color: c.color }}>-</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );

  return (
    <div data-testid="analysis-section">
      <Tabs defaultValue="swot">
        <TabsList>
          <TabsTrigger value="swot" data-testid="analysis-tab-swot">SWOT Analysis</TabsTrigger>
          <TabsTrigger value="pestel" data-testid="analysis-tab-pestel">PESTEL Analysis</TabsTrigger>
        </TabsList>

        {/* SWOT */}
        <TabsContent value="swot">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Strengths, Weaknesses, Opportunities, Threats</p>
                {(swot.contributors || []).length > 0 && <p className="text-xs text-gray-500">Contributors: {swot.contributors.join(', ')}</p>}
              </div>
              {canManage && !editingSwot && (
                <Button size="sm" variant="outline" onClick={openSwotEditor} data-testid="edit-swot-btn">
                  {hasSwotData ? 'Edit' : 'Create'} SWOT
                </Button>
              )}
            </div>

            {editingSwot && editSwot ? (
              <>
                {renderSwotGrid(editSwot, SWOT_CONFIG, true, setEditSwot)}
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" size="sm" onClick={() => setEditingSwot(false)}>Cancel</Button>
                  <Button size="sm" onClick={handleSaveSwot} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-swot-btn"><Save className="h-3.5 w-3.5 mr-1" /> Save SWOT</Button>
                </div>
              </>
            ) : hasSwotData ? (
              renderSwotGrid(swot, SWOT_CONFIG, false, null)
            ) : (
              <Card className="border-dashed"><CardContent className="py-8 text-center text-sm text-gray-500">
                No SWOT analysis yet. {canManage && 'Click "Create SWOT" to start.'}
              </CardContent></Card>
            )}
          </div>
        </TabsContent>

        {/* PESTEL */}
        <TabsContent value="pestel">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Political, Economic, Social, Technological, Environmental, Legal</p>
                {(pestel.contributors || []).length > 0 && <p className="text-xs text-gray-500">Contributors: {pestel.contributors.join(', ')}</p>}
              </div>
              {canManage && !editingPestel && (
                <Button size="sm" variant="outline" onClick={openPestelEditor} data-testid="edit-pestel-btn">
                  {hasPestelData ? 'Edit' : 'Create'} PESTEL
                </Button>
              )}
            </div>

            {editingPestel && editPestel ? (
              <>
                {renderPestelGrid(editPestel, PESTEL_CONFIG, true, setEditPestel)}
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" size="sm" onClick={() => setEditingPestel(false)}>Cancel</Button>
                  <Button size="sm" onClick={handleSavePestel} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-pestel-btn"><Save className="h-3.5 w-3.5 mr-1" /> Save PESTEL</Button>
                </div>
              </>
            ) : hasPestelData ? (
              renderPestelGrid(pestel, PESTEL_CONFIG, false, null)
            ) : (
              <Card className="border-dashed"><CardContent className="py-8 text-center text-sm text-gray-500">
                No PESTEL analysis yet. {canManage && 'Click "Create PESTEL" to start.'}
              </CardContent></Card>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PDAnalysis;
